<template>
  <ul
    role="list"
    class="space-y-1"
  >
    <!-- Loading skeleton -->
    <template v-if="loading || !initialized">
      <li
        v-for="i in skeletonCount"
        :key="`skeleton-${i}`"
        class="px-2 py-2"
      >
        <div class="flex gap-x-3 items-center">
          <div
            class="animate-pulse bg-surface-level-200 dark:bg-surface-level-700 rounded shrink-0 size-6"
          />

          <div
            class="animate-pulse bg-surface-level-200 dark:bg-surface-level-700 h-4 rounded"
            :style="{ width: `${60 + (i % 3) * 15}%` }"
          />
        </div>
      </li>
    </template>

    <!-- Empty state -->
    <li
      v-else-if="menuItems.length === 0"
      class="dark:text-content-level-400 px-2 py-3 text-content-level-500 text-sm"
    >
      <slot name="empty">
        {{ $t('dashboard.labels.noNavigationAvailable') }}
      </slot>
    </li>

    <!-- Menu items -->
    <template v-else>
      <li
        v-for="item in menuItems"
        :key="item.key || item.route || item.label"
      >
        <NuxtLink
          :to="item.route || '#'"
          class="flex font-semibold gap-x-3 group p-2 rounded-md text-sm transition-colors"
          :class="[isActive(item) ? activeClasses : inactiveClasses]"
        >
          <component
            :is="item.icon"
            class="shrink-0 size-6 transition-colors"
            :class="[isActive(item) ? iconActiveClasses : iconInactiveClasses]"
            aria-hidden="true"
          />

          <span class="truncate">{{ item.label }}</span>
        </NuxtLink>
      </li>
    </template>
  </ul>
</template>

<script setup lang="ts">
import { computed, type Component } from 'vue'
import { useRoute } from 'vue-router'

interface MenuItem {
  key?: string | number
  route?: string
  label?: string
  icon?: Component
}

const props = withDefaults(
  defineProps<{
    /** Complete menu item set used for sidebar state and filtering. */
    allMenuItems?: MenuItem[]
    /** Whether sidebar data has finished initialization. */
    initialized?: boolean
    /** Whether sidebar data is loading. */
    loading?: boolean
    /** Menu items currently visible in the sidebar. */
    menuItems?: MenuItem[]
    /** Number of skeleton rows displayed while loading. */
    skeletonCount?: number
    /** Sidebar presentation variant. */
    variant?: 'mobile' | 'desktop'
  }>(),
  {
    allMenuItems: () => [],
    menuItems: () => [],
    skeletonCount: 5,
    variant: 'desktop'
  }
)

const route = useRoute()

const activeClasses = computed(() =>
  props.variant === 'mobile'
    ? 'bg-surface-level-50 text-primary dark:bg-surface-canvas/5'
    : 'bg-surface-level-50 text-primary dark:bg-surface-canvas/5'
)

const inactiveClasses = computed(() =>
  props.variant === 'mobile'
    ? 'text-content-level-700 hover:bg-surface-level-50 hover:text-primary dark:text-content-level-300 dark:hover:bg-surface-canvas/5'
    : 'text-content-level-700 hover:bg-surface-level-50 hover:text-primary dark:text-content-level-400 dark:hover:bg-surface-canvas/5'
)

const iconActiveClasses = computed(() =>
  props.variant === 'mobile' ? 'text-primary' : 'text-primary'
)

const iconInactiveClasses = computed(() =>
  props.variant === 'mobile'
    ? 'text-content-level-400 group-hover:text-primary '
    : 'text-content-level-400 group-hover:text-primary '
)

const isActive = (item: MenuItem) => {
  if (item.route === undefined || item.route.length === 0) return false

  const currentPath = route.path
  const itemPath = item.route

  // Exact match - always active
  if (currentPath === itemPath) return true

  // For parent routes (when current path starts with item path)
  // Only mark as active if there's no more specific menu item that matches
  if (currentPath.startsWith(`${itemPath}/`)) {
    // Use allMenuItems if provided, otherwise fall back to menuItems
    const itemsToCheck = props.allMenuItems.length > 0 ? props.allMenuItems : props.menuItems

    // Find the most specific matching menu item
    const matchingItems = itemsToCheck
      .filter((otherItem: MenuItem) => {
        const otherPath = otherItem.route
        if (otherPath === undefined || otherPath.length === 0) return false
        return currentPath === otherPath || currentPath.startsWith(`${otherPath}/`)
      })
      .sort((a: MenuItem, b: MenuItem) => (b.route?.length ?? 0) - (a.route?.length ?? 0))

    // This item is active only if it's the most specific match
    return matchingItems[0]?.route === itemPath
  }

  return false
}

defineSlots<{
  empty?: () => unknown
  categoryAside?: () => unknown
}>()
</script>
